import { FunctionComponent } from "react";

const SECTION_HEADING = "text-primary-blue font-semibold text-ssmd mt-8 mb-3";
const SUBSECTION_HEADING = "text-primary-blue font-semibold text-ssm mt-5 mb-2";
const PARAGRAPH = "text-primary-gray text-sm leading-6 mb-3";
const LIST = "list-disc pl-6 text-primary-gray text-sm leading-6 mb-3 space-y-1";
const TABLE_WRAPPER = "w-full overflow-x-auto mb-4";
const TABLE = "w-full text-left border border-border text-sm";
const TH = "border border-border bg-gray-25 text-primary-blue font-semibold px-3 py-2 align-top";
const TD = "border border-border text-primary-gray px-3 py-2 align-top";

const stammdatenRows: Array<[string, string, string]> = [
	["Vorname, Nachname", "Ja", "Identifikation und Anzeige im System"],
	["E-Mail-Adresse", "Ja", "Login, Kommunikation, Benachrichtigungen"],
	["Passwort (bcrypt-Hash)", "Ja", "Authentifizierung"],
	["Bevorzugte Sprache (DE/EN)", "Ja", "Spracheinstellung der Oberfläche"],
	["Rolle (Admin, User, Participant)", "Ja", "Berechtigungssteuerung"],
	["Firmenzugehörigkeit", "Ja", "Zuordnung zum Unternehmenskonto"],
	["LinkedIn-URL", "Nein", "Optionales Profilfeld"],
	["2FA-Secret (AES-256-verschlüsselt)", "Nein", "Optionale Zwei-Faktor-Authentifizierung"],
	["API-Key", "Automatisch", "Programmatischer Zugang zur Schnittstelle"],
];

const tomRows: Array<[string, string]> = [
	[
		"Passwort-Hashing",
		"Passwörter werden mit bcrypt und Salt Rounds gehasht und niemals im Klartext gespeichert.",
	],
	[
		"Zwei-Faktor-Authentifizierung",
		"Optionale 2FA mittels TOTP (Authenticator-Apps). Die Secrets werden AES-256-verschlüsselt gespeichert.",
	],
	[
		"Rate Limiting",
		"Schutz vor Brute-Force- und Denial-of-Service-Angriffen durch konfigurierbare Anfragebegrenzung.",
	],
	[
		"Sicherheits-HTTP-Header",
		"Einsatz von Helmet.js für empfohlene Sicherheits-Header (z. B. CSP, HSTS).",
	],
	["CORS-Konfiguration", "Cross-Origin-Anfragen sind auf erlaubte Domains beschränkt."],
	[
		"Log-Redaktion",
		"Passwörter und Authentifizierungs-Header werden automatisch aus Protokolldateien entfernt.",
	],
	[
		"Eingabevalidierung",
		"Alle Nutzereingaben werden serverseitig validiert (Class-Validator / ValidationPipe).",
	],
	[
		"Verschlüsselung",
		"Sensible Daten (2FA-Secrets) werden mit AES-256 verschlüsselt. Transport über TLS/HTTPS.",
	],
	[
		"Hosting in Deutschland",
		"Sämtliche Infrastruktur wird auf Hetzner Cloud in Deutschland betrieben.",
	],
];

const aufbewahrungRows: Array<[string, string]> = [
	[
		"Benutzerkonto",
		"Für die Dauer der Vertragsbeziehung. Nach Kündigung gemäß den definierten Aufbewahrungsfristen.",
	],
	[
		"Trainingsfortschritt / Protokolle",
		"Für die Dauer der Vertragsbeziehung bzw. gemäß vertraglicher Vereinbarung mit dem Unternehmenskunden.",
	],
	["Audit-Logs", "Gemäß den definierten Aufbewahrungsfristen zur Nachvollziehbarkeit."],
	[
		"Authentifizierungs-Tokens",
		"Access Tokens: kurzlebig. Refresh Tokens: konfigurierbare Laufzeit. Einladungs-/Reset-Tokens: zeitlich begrenzt.",
	],
	["Chat-Nachrichten (Redis-Cache)", "Temporär im Cache; werden nach Sitzungsende gelöscht."],
];

const rechteRows: Array<[string, string, string]> = [
	["Auskunft", "Sie können Auskunft über Ihre gespeicherten Daten verlangen.", "Art. 15 DSGVO"],
	["Berichtigung", "Sie können die Berichtigung unrichtiger Daten verlangen.", "Art. 16 DSGVO"],
	[
		"Löschung",
		"Sie können die Löschung Ihrer Daten verlangen, sofern keine Aufbewahrungspflichten entgegenstehen.",
		"Art. 17 DSGVO",
	],
	["Einschränkung", "Sie können die Einschränkung der Verarbeitung verlangen.", "Art. 18 DSGVO"],
	[
		"Datenübertragbarkeit",
		"Sie können Ihre Daten in einem maschinenlesbaren Format erhalten.",
		"Art. 20 DSGVO",
	],
	[
		"Widerspruch",
		"Sie können der Verarbeitung auf Basis berechtigter Interessen widersprechen.",
		"Art. 21 DSGVO",
	],
	[
		"Widerruf der Einwilligung",
		"Sie können eine erteilte Einwilligung jederzeit widerrufen.",
		"Art. 7 Abs. 3 DSGVO",
	],
];

export const PrivacyPolicy: FunctionComponent = () => {
	return (
		<div className="w-full h-[calc(100vh-50px)] bg-white overflow-y-auto">
			<div className="max-w-page mx-auto px-6 py-10">
				<h1 className="text-primary-blue font-semibold text-md mb-2">Datenschutzerklärung</h1>
				<p className="text-primary-gray-lighter text-sm mb-1">
					BCM360 – Plattform für Business Continuity Management
				</p>
				<p className="text-primary-gray-lighter text-sm mb-6">Stand: März 2026</p>

				<h2 className={SECTION_HEADING}>1. Verantwortlicher</h2>
				<p className={PARAGRAPH}>
					Verantwortlicher im Sinne der Datenschutz-Grundverordnung (DSGVO) und sonstiger
					datenschutzrechtlicher Bestimmungen ist:
				</p>
				<p className={PARAGRAPH}>
					MACONIA GmbH
					<br />
					Kurfürstendamm 11, 10719 Berlin
					<br />
					E-Mail: kontakt(at)maconia.de
					<br />
					Website: https://app.bcm360.de
				</p>
				<p className={PARAGRAPH}>
					Die technische Entwicklung und der Betrieb der Plattform erfolgen durch Browserbite EOOD.
				</p>

				<h2 className={SECTION_HEADING}>2. Allgemeines zur Datenverarbeitung</h2>
				<p className={PARAGRAPH}>
					BCM360 ist eine Plattform für Business Continuity Management (BCM). Die Plattform
					ermöglicht die Durchführung von Krisenszenarien, Trainings und Übungen für Unternehmen.
					Die Verarbeitung personenbezogener Daten erfolgt grundsätzlich nur, soweit dies zur
					Bereitstellung der Plattform und ihrer Funktionen erforderlich ist.
				</p>
				<p className={PARAGRAPH}>
					Sämtliche Daten werden auf Servern der Hetzner Online GmbH in Deutschland gehostet. Eine
					Übermittlung personenbezogener Daten an Drittländer findet nur in den unter Abschnitt 8
					genannten Fällen statt.
				</p>

				<h2 className={SECTION_HEADING}>3. Rechtsgrundlagen der Verarbeitung</h2>
				<p className={PARAGRAPH}>
					Die Verarbeitung Ihrer personenbezogenen Daten erfolgt auf Basis folgender
					Rechtsgrundlagen:
				</p>
				<ul className={LIST}>
					<li>
						<strong>Art. 6 Abs. 1 lit. b DSGVO</strong> – soweit die Verarbeitung zur Erfüllung
						eines Vertrags oder zur Durchführung vorvertraglicher Maßnahmen erforderlich ist (z. B.
						Registrierung, Trainingsteilnahme).
					</li>
					<li>
						<strong>Art. 6 Abs. 1 lit. f DSGVO</strong> – soweit die Verarbeitung zur Wahrung
						berechtigter Interessen erforderlich ist (z. B. Sicherheitsmaßnahmen,
						Audit-Protokollierung, Missbrauchsprävention).
					</li>
					<li>
						<strong>Art. 6 Abs. 1 lit. a DSGVO</strong> – soweit Sie eine Einwilligung erteilt haben
						(z. B. optionale Zwei-Faktor-Authentifizierung).
					</li>
				</ul>

				<h2 className={SECTION_HEADING}>4. Art der erhobenen personenbezogenen Daten</h2>

				<h3 className={SUBSECTION_HEADING}>4.1 Benutzerkonto und Stammdaten</h3>
				<p className={PARAGRAPH}>
					Bei der Registrierung und Nutzung der Plattform werden folgende Daten erhoben und
					gespeichert:
				</p>
				<div className={TABLE_WRAPPER}>
					<table className={TABLE}>
						<thead>
							<tr>
								<th className={TH}>Datenpunkt</th>
								<th className={TH}>Pflichtangabe</th>
								<th className={TH}>Zweck</th>
							</tr>
						</thead>
						<tbody>
							{stammdatenRows.map(([field, required, purpose]) => (
								<tr key={field}>
									<td className={TD}>{field}</td>
									<td className={TD}>{required}</td>
									<td className={TD}>{purpose}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				<h3 className={SUBSECTION_HEADING}>4.2 Authentifizierungsdaten</h3>
				<p className={PARAGRAPH}>
					Für die sichere Anmeldung werden folgende technische Daten verarbeitet:
				</p>
				<ul className={LIST}>
					<li>JWT Access Token und Refresh Token (im localStorage des Browsers gespeichert)</li>
					<li>2FA-TOTP-Secrets (AES-256-CBC/GCM verschlüsselt in der Datenbank)</li>
					<li>Einladungs- und Passwort-Reset-Tokens (zeitlich begrenzt gültig)</li>
				</ul>

				<h3 className={SUBSECTION_HEADING}>4.3 Trainings- und Szenariodaten</h3>
				<p className={PARAGRAPH}>
					Im Rahmen der Nutzung der Trainingsplattform werden folgende Daten erhoben:
				</p>
				<ul className={LIST}>
					<li>Teilnahme an Szenarien und Kursen (inkl. Start-/Endzeit)</li>
					<li>Getroffene Entscheidungen und Phasenverlauf innerhalb der Szenarien</li>
					<li>Protokollnachrichten (mit Benutzer-ID, Name, E-Mail und Zeitstempel)</li>
					<li>Teilnahme-Codes zum Beitritt</li>
					<li>Ergebnis der Übung (bestanden / nicht bestanden)</li>
					<li>Von Teilnehmern während Übungen hochgeladene Dokumente</li>
				</ul>

				<h3 className={SUBSECTION_HEADING}>4.4 Chat- und Kommunikationsdaten</h3>
				<p className={PARAGRAPH}>
					Die Plattform ermöglicht Echtzeitkommunikation zwischen Teilnehmern:
				</p>
				<ul className={LIST}>
					<li>In-App-Chat-Nachrichten (über Socket.IO, temporär in Redis gecacht)</li>
					<li>
						Protokollnachrichten mit Benutzer-ID, Name, E-Mail, Nachrichtentext und Zeitstempel
					</li>
					<li>Hand-Raising-Status (Meldefunktion für Teilnehmer)</li>
				</ul>

				<h3 className={SUBSECTION_HEADING}>4.5 Audio-/Sprachkommunikation</h3>
				<p className={PARAGRAPH}>
					Für die Sprachübertragung zwischen Trainingsteilnehmern wird LiveKit (WebRTC) eingesetzt:
				</p>
				<ul className={LIST}>
					<li>Audio-Streams zwischen Teilnehmern (nur Audio, kein Video)</li>
					<li>Benutzer-ID und Benutzername werden als Identität im Token übermittelt</li>
					<li>Es werden keine Audio-Aufnahmen dauerhaft gespeichert.</li>
				</ul>

				<h3 className={SUBSECTION_HEADING}>4.6 VR-App-spezifische Daten (Apple Vision Pro)</h3>
				<p className={PARAGRAPH}>
					Bei Nutzung der VR-App werden zusätzlich folgende Daten verarbeitet:
				</p>
				<ul className={LIST}>
					<li>Login über dieselbe API (E-Mail/Passwort)</li>
					<li>Anzeige von Vorname, Nachname und E-Mail in der VR-Oberfläche</li>
					<li>Socket.IO-Verbindung mit Client-Typ und JWT-Token</li>
					<li>
						Multiplayer-Netzwerk über Photon Fusion (sitzungsbasierte Räume,
						Spielerposition/-status)
					</li>
					<li>Mikrofonzugriff für VR-Multiplayer (nach Zustimmung des Geräts)</li>
					<li>Es werden keine zusätzlichen Telemetriedaten erhoben.</li>
				</ul>

				<h2 className={SECTION_HEADING}>5. Cookies und lokale Speicherung</h2>
				<p className={PARAGRAPH}>
					BCM360 verwendet keine Cookies. Die Plattform setzt keine Tracking-Cookies, Analyse-Tools
					(wie Google Analytics oder Matomo) oder sonstige Drittanbieter-Tracker ein.
				</p>
				<p className={PARAGRAPH}>
					Ausschließlich der localStorage des Browsers wird genutzt, um den
					JWT-Authentifizierungs-Token zu speichern. Dies ist technisch notwendig, um eine
					angemeldete Sitzung aufrechtzuerhalten.
				</p>

				<h2 className={SECTION_HEADING}>6. Audit-Log und Ereignisprotokoll</h2>
				<h3 className={SUBSECTION_HEADING}>6.1 Audit-Log</h3>
				<p className={PARAGRAPH}>
					Die Plattform protokolliert automatisch Änderungen an Datenbankeinträgen zu Zwecken der
					Nachvollziehbarkeit und Integritätssicherung. Erfasst werden:
				</p>
				<ul className={LIST}>
					<li>Tabellenname und Datensatz-ID</li>
					<li>Benutzer-ID und Unternehmens-ID</li>
					<li>Art der Änderung (Erstellen/Ändern/Löschen)</li>
					<li>Alter und neuer Datenstand</li>
					<li>Zeitstempel</li>
				</ul>
				<p className={PARAGRAPH}>
					Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der
					Nachvollziehbarkeit von Datenänderungen).
				</p>

				<h3 className={SUBSECTION_HEADING}>6.2 Ereignisprotokoll (Event History)</h3>
				<p className={PARAGRAPH}>
					Benutzeraktionen wie Anmeldungen und E-Mail-Bestätigungen werden mit Event-Name,
					Benutzer-ID, zusätzlichen Metadaten und Zeitstempel protokolliert.
				</p>

				<h2 className={SECTION_HEADING}>7. Technische und organisatorische Maßnahmen</h2>
				<p className={PARAGRAPH}>
					Zum Schutz Ihrer personenbezogenen Daten setzen wir folgende Maßnahmen ein:
				</p>
				<div className={TABLE_WRAPPER}>
					<table className={TABLE}>
						<thead>
							<tr>
								<th className={TH}>Maßnahme</th>
								<th className={TH}>Beschreibung</th>
							</tr>
						</thead>
						<tbody>
							{tomRows.map(([measure, description]) => (
								<tr key={measure}>
									<td className={TD}>{measure}</td>
									<td className={TD}>{description}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				<h2 className={SECTION_HEADING}>8. Drittanbieter-Dienste und Datenübermittlungen</h2>
				<p className={PARAGRAPH}>
					Zur Erbringung bestimmter Funktionen werden folgende Drittanbieter-Dienste eingesetzt:
				</p>

				<h3 className={SUBSECTION_HEADING}>8.1 OpenAI (GPT-5) – KI-Szenariogenerierung</h3>
				<ul className={LIST}>
					<li>Zweck: Automatische Generierung von BCM-Krisenszenarien</li>
					<li>
						Übermittelte Daten: Branche, Unternehmensgröße, Vorfalltyp, Defizite, zusätzliche
						Notizen, Sprache
					</li>
					<li>Es werden keine direkt personenbezogenen Daten übermittelt</li>
					<li>Serverstandort: USA</li>
					<li>Rechtsgrundlage: Art. 6 Abs. 1 lit. b bzw. lit. f DSGVO</li>
					<li>
						Garantien für Drittlandtransfer: EU-US Data Privacy Framework /
						EU-Standardvertragsklauseln (SCCs)
					</li>
				</ul>

				<h3 className={SUBSECTION_HEADING}>8.2 Google Generative AI (Gemini)</h3>
				<ul className={LIST}>
					<li>Zweck: Konfiguriert als alternative KI-Lösung</li>
					<li>
						Übermittelte Daten: Gleiche Art wie bei OpenAI (keine direkt personenbezogenen Daten)
					</li>
					<li>Serverstandort: USA</li>
					<li>Garantien: EU-US Data Privacy Framework / SCCs</li>
				</ul>

				<h3 className={SUBSECTION_HEADING}>8.3 ElevenLabs – Text-to-Speech</h3>
				<ul className={LIST}>
					<li>Zweck: Umwandlung von Szenariotexten in Sprache</li>
					<li>Übermittelte Daten: Szenariotexte (max. 5.000 Zeichen), Voice-ID, Sprache</li>
					<li>Es werden keine personenbezogenen Daten übermittelt</li>
					<li>
						Generierte Audiodateien werden als MP3 in Hetzner Object Storage (Deutschland)
						gespeichert
					</li>
					<li>Serverstandort: USA</li>
					<li>Garantien: EU-US Data Privacy Framework / SCCs</li>
				</ul>

				<h3 className={SUBSECTION_HEADING}>8.4 LiveKit – Echtzeit-Audiokommunikation</h3>
				<ul className={LIST}>
					<li>Zweck: Voice-Chat zwischen Trainingsteilnehmern (WebRTC)</li>
					<li>
						Übermittelte Daten: Benutzer-ID, Benutzername (als Identität im Token), Audio-Stream
					</li>
					<li>
						Die Übertragung erfolgt WebRTC-basiert (Peer-to-Peer mit TURN/STUN-Serververmittlung)
					</li>
				</ul>

				<h3 className={SUBSECTION_HEADING}>8.5 Mailjet – E-Mail-Versand</h3>
				<ul className={LIST}>
					<li>
						Zweck: Versand von System-E-Mails (Einladungen, Passwort-Reset, E-Mail-Bestätigung)
					</li>
					<li>Übermittelte Daten: E-Mail-Adresse, Vorname, Nachname des Empfängers</li>
					<li>Anbieter: Mailjet SAS, Frankreich (EU)</li>
					<li>Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO</li>
				</ul>

				<h3 className={SUBSECTION_HEADING}>8.6 Photon Fusion / Exit Games – VR-Multiplayer</h3>
				<ul className={LIST}>
					<li>Zweck: Multiplayer-Netzwerk für die VR-App</li>
					<li>Übermittelte Daten: Session-Name, Spielerposition/-status</li>
					<li>Anbieter: Exit Games GmbH, Deutschland</li>
					<li>Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO</li>
				</ul>

				<h3 className={SUBSECTION_HEADING}>8.7 Hetzner Online GmbH – Hosting</h3>
				<ul className={LIST}>
					<li>Zweck: Hosting sämtlicher Infrastruktur (Server, Datenbanken, Object Storage)</li>
					<li>Standort: Deutschland</li>
					<li>Es besteht ein Auftragsverarbeitungsvertrag (AVV) gem. Art. 28 DSGVO</li>
				</ul>

				<h2 className={SECTION_HEADING}>9. Speicherung und Aufbewahrung</h2>
				<p className={PARAGRAPH}>
					Ihre personenbezogenen Daten werden so lange gespeichert, wie es für die Erfüllung der
					jeweiligen Verarbeitungszwecke erforderlich ist oder gesetzliche Aufbewahrungspflichten
					bestehen.
				</p>
				<div className={TABLE_WRAPPER}>
					<table className={TABLE}>
						<thead>
							<tr>
								<th className={TH}>Datenkategorie</th>
								<th className={TH}>Aufbewahrung</th>
							</tr>
						</thead>
						<tbody>
							{aufbewahrungRows.map(([category, retention]) => (
								<tr key={category}>
									<td className={TD}>{category}</td>
									<td className={TD}>{retention}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				<p className={PARAGRAPH}>
					Gelöschte Daten werden zunächst als gelöscht markiert (Soft Delete) und nach Ablauf der
					Aufbewahrungsfrist endgültig entfernt.
				</p>

				<h2 className={SECTION_HEADING}>10. Ihre Rechte als betroffene Person</h2>
				<p className={PARAGRAPH}>Sie haben gemäß der DSGVO folgende Rechte:</p>
				<div className={TABLE_WRAPPER}>
					<table className={TABLE}>
						<thead>
							<tr>
								<th className={TH}>Recht</th>
								<th className={TH}>Beschreibung</th>
								<th className={TH}>Grundlage</th>
							</tr>
						</thead>
						<tbody>
							{rechteRows.map(([right, description, basis]) => (
								<tr key={right}>
									<td className={TD}>{right}</td>
									<td className={TD}>{description}</td>
									<td className={TD}>{basis}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				<p className={PARAGRAPH}>
					Zur Ausübung Ihrer Rechte wenden Sie sich bitte an: datenschutz@bcm360.de.
				</p>
				<p className={PARAGRAPH}>
					Darüber hinaus haben Sie das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu
					beschweren (Art. 77 DSGVO).
				</p>

				<h2 className={SECTION_HEADING}>11. Datenübermittlung in Drittländer</h2>
				<p className={PARAGRAPH}>
					Im Rahmen der Nutzung von OpenAI, Google Generative AI und ElevenLabs werden Daten an
					Server in den USA übermittelt. Diese Anbieter sind entweder unter dem EU-US Data Privacy
					Framework zertifiziert oder es bestehen EU-Standardvertragsklauseln (SCCs) gemäß Art. 46
					Abs. 2 lit. c DSGVO als Grundlage für den Datentransfer.
				</p>
				<p className={PARAGRAPH}>
					Wir weisen darauf hin, dass bei einer Übermittlung in die USA ein Risiko besteht, dass
					US-Behörden auf die Daten zugreifen könnten. Es werden jedoch keine direkt
					personenbezogenen Daten an diese Dienste übermittelt.
				</p>

				<h2 className={SECTION_HEADING}>12. Änderungen dieser Datenschutzerklärung</h2>
				<p className={PARAGRAPH}>
					Wir behalten uns vor, diese Datenschutzerklärung bei Bedarf anzupassen, um sie an
					geänderte Rechtslagen, technische Änderungen oder neue Funktionen der Plattform
					anzupassen. Die jeweils aktuelle Fassung ist auf der Plattform verfügbar.
				</p>
			</div>
		</div>
	);
};
